import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SpeedDial from '@mui/material/SpeedDial';
import { useTheme } from '@mui/material/styles'; // Importa useTheme
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { useSelector } from 'react-redux'; // <--- Importa useSelector

const HelpGuideDrawerStakeholders = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  // Recupera i ruoli dell'utente dallo stato Redux
  const { user } = useSelector((state) => state.auth);
  // Determina se l'utente è un admin (o ha il ruolo 'admin')
  const isCurrentUserAdmin = user && user.roles && user.roles.includes('admin');

  const theme = useTheme(); // Inizializza useTheme

  const toggleDrawer = (newOpen) => () => {
    setOpenDrawer(newOpen);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const renderSectionContent = (sectionTitle) => {
    switch (sectionTitle) {
      case 'introduzione':
        return (
          <Typography variant="body1" paragraph>
            Benvenuto nella sezione di gestione dei contatti! Sono qui per aiutarti a capire come utilizzare questa tabella.
          </Typography>
        );
      case 'visualizzazione_tabella':
        return (
          <>
            <Typography variant="body1" paragraph>
              La tabella mostra un elenco di tutti i contatti (Stakeholders) registrati. Puoi vedere diverse colonne con informazioni come "Nome", "Cognome", "Ragione sociale", "Categoria" e altre.
            </Typography>
            <ul>
              <li><strong>Colonne Visibili:</strong> Di default, alcune colonne sono nascoste per mantenere la tabella pulita. Puoi scegliere quali colonne visualizzare usando il pulsante "Mostra colonne" (icona con tre colonne).</li>
              <li><strong>Impaginazione:</strong> I contatti sono divisi in pagine. Puoi navigare tra le pagine e modificare il numero di righe per pagina (<code>5, 10, 20, 50</code>).</li>
            </ul>
          </>
        );
      case 'ricerca_filtro':
        return (
          <>
            <Typography variant="body1" paragraph>
              Puoi usare la barra di ricerca o i filtri per trovare rapidamente i contatti:
            </Typography>
            <ul>
              <li><strong>Barra di Ricerca Globale:</strong> In alto a destra della tabella, c'è una barra di ricerca. Digita qualsiasi termine (es. nome, cognome, ragione sociale) e la tabella filtrerà i risultati in tempo reale su tutte le colonne visibili.</li>
              <li><strong>Filtri per Colonna:</strong> Puoi filtrare i dati per ogni singola colonna cliccando sull'icona del filtro (imbuto) nell'intestazione della colonna desiderata.
                <ul>
                  <li>Per la colonna "Primo contatto", puoi filtrare per anno (es. <code>2024</code>) o per data specifica nel formato <code>GG/MM/AAAA</code>.</li>
                </ul>
              </li>
            </ul>
          </>
        );
      case 'esportazione_dati':
        return (
          <>
            <Typography variant="body1" paragraph>
              Sì, puoi esportare i dati della tabella in diversi formati.
            </Typography>
            <ul>
              <li><strong>Pulsante Esporta:</strong> Clicca sull'icona "Esporta" (freccia verso il basso con riga) nella barra degli strumenti della tabella. Puoi scegliere di esportare solo i dati visibili o tutti i dati.</li>
            </ul>
          </>
        );
      case 'gestione_contatti':
        return (
          <>
            <Typography variant="body1" paragraph>
              Per ogni riga (contatto) nella tabella, sono disponibili diverse azioni. Puoi trovarle nella colonna "Azioni" a destra.
            </Typography>
            <ul>
              <li><strong>Modifica Contatto:</strong>
                <ul>
                  <li><strong>Icona:</strong> Matita (Edit).</li>
                  <li><strong>Azione:</strong> Apre una nuova finestra (o scheda) del browser con il modulo per modificare i dettagli del contatto selezionato.</li>
                  {!isCurrentUserAdmin && (
                    <li><strong>Accesso:</strong> Questa funzionalità è **disabilitata se non sei Amministratore**.</li>
                  )}
                </ul>
              </li>
              <li><strong>Registra Azione (per il Contatto):</strong>
                <ul>
                  <li><strong>Icona:</strong> Cerchio con il segno più e un contorno (AddCircleOutlineIcon).</li>
                  <li><strong>Azione:</strong> Ti reindirizza a una pagina dove puoi inserire una nuova attività (chiamata anche "azione" o "intervento") associata direttamente a quel contatto.</li>
                </ul>
              </li>
              <li><strong>Cancella Contatto:</strong>
                <ul>
                  <li><strong>Icona:</strong> Cestino (DeleteOutline).</li>
                  <li><strong>Azione:</strong> Consente di eliminare definitivamente il contatto selezionato.</li>
                  <li><strong>Avviso:</strong> Verrà richiesta una conferma prima della cancellazione, poiché l'operazione è irreversibile.</li>
                  {!isCurrentUserAdmin && (
                    <li><strong>Accesso:</strong> Questa funzionalità è **disabilitata se non sei Amministratore**.</li>
                  )}
                </ul>
              </li>
            </ul>
          </>
        );
      case 'aggiungi_nuovo_contatto':
        return (
          <>
            <Typography variant="body1" paragraph>
              In cima alla pagina, prima della tabella, trovi un pulsante.
            </Typography>
            <ul>
              <li><strong>Pulsante:</strong> **"Aggiungi contatto"** (con icona <code>+</code>).</li>
              <li><strong>Azione:</strong> Cliccando questo pulsante, verrai reindirizzato al modulo di inserimento di un nuovo contatto.</li>
            </ul>
          </>
        );
      case 'azioni_tabella_material':
          return (
            <>
              <Typography variant="body1" paragraph>
                Questa tabella interattiva offre diverse funzionalità per la gestione e l'analisi dei dati dei contatti.
              </Typography>
              <ul>
                <li><strong>Barra degli strumenti della tabella:</strong> In alto, sopra le colonne, trovi una serie di icone per diverse azioni:
                  <ul>
                    <li><strong>Cerca (lente di ingrandimento):</strong> Apre/Chiude la barra di ricerca globale.</li>
                    <li><strong>Esporta (freccia verso il basso con riga):</strong> Permette di esportare i dati visibili o tutti i dati della tabella.</li>
                    <li><strong>Mostra Colonne (tre colonne verticali):</strong> Permette di selezionare quali colonne visualizzare o nascondere.</li>
                    <li><strong>Filtra (imbuto):</strong> Abilita/Disabilita le barre di filtro sotto le intestazioni delle colonne.</li>
                    <li><strong>Group by (icona raggruppamento):</strong> Trascina l'intestazione di una colonna qui per raggruppare le righe in base ai valori di quella colonna.</li>
                    <li><strong>Paging (frecce e numeri):</strong> Naviga tra le pagine della tabella e cambia il numero di righe per pagina.</li>
                  </ul>
                </li>
                <li><strong>Ordinamento delle Colonne:</strong> Clicca sull'intestazione di una colonna per ordinare i dati in ordine crescente o decrescente.</li>
                <li><strong>Filtri per Colonna:</strong> Se la funzionalità di filtro è attiva, puoi digitare direttamente sotto l'intestazione di una colonna per filtrare i risultati specifici per quella colonna.</li>
                <li><strong>Ridimensionamento Colonne:</strong> Passa il mouse tra due intestazioni di colonna finché il cursore non cambia forma, quindi clicca e trascina per modificare la larghezza della colonna.</li>
              </ul>
            </>
          );
      case 'problemi_comuni':
        return (
          <>
            <ul>
              <li><strong>Domanda:</strong> Perché alcune opzioni sono disabilitate per me?
                <br />
                <strong>Risposta:</strong> Le funzionalità di **Modifica** e **Cancella** i contatti sono disabilitate se **non sei Amministratore**, per ragioni di sicurezza e permessi.</li>
            </ul>
          </>
        );
      default:
        return null;
    }
  };

  const accordionSections = [
    { id: 'introduzione', title: 'Benvenuto nella Guida!', content: renderSectionContent('introduzione') },
    { id: 'visualizzazione_tabella', title: '1. Visualizzazione della Tabella Contatti', content: renderSectionContent('visualizzazione_tabella') },
    { id: 'ricerca_filtro', title: '2. Funzionalità di Ricerca e Filtro', content: renderSectionContent('ricerca_filtro') },
    { id: 'esportazione_dati', title: '3. Esportazione Dati', content: renderSectionContent('esportazione_dati') },
    { id: 'gestione_contatti', title: '4. Gestione dei Contatti (Azioni)', content: renderSectionContent('gestione_contatti') },
    { id: 'aggiungi_nuovo_contatto', title: '5. Aggiungi Nuovo Contatto', content: renderSectionContent('aggiungi_nuovo_contatto') },
    { id: 'azioni_tabella_material', title: '6. Funzionalità della Tabella MaterialTable', content: renderSectionContent('azioni_tabella_material') },
    { id: 'problemi_comuni', title: '7. Problemi Comuni', content: renderSectionContent('problemi_comuni') },
  ];

  return (
    <>
      <SpeedDial
        ariaLabel="Hai bisogno di aiuto?"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<HelpOutlineIcon />}
        onClick={toggleDrawer(true)}
        FabProps={{
            sx: {
                bgcolor: 'primary.main',
                '&:hover': {
                    bgcolor: 'primary.dark',
                },
            }
        }}
        tooltipTitle="Hai bisogno di aiuto?"
        tooltipOpen
      >
      </SpeedDial>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 450, md: 550 } },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            // Aggiungi padding-top al contenitore principale del header del drawer
            // Questo spingerà il contenuto sotto l'AppBar principale, se fissa
            pt: { xs: `calc(${theme.spacing(2)} + 56px)`, sm: `calc(${theme.spacing(2)} + 64px)` }, // theme.spacing(2) è il p:2 iniziale, 56/64px sono altezze AppBar
            borderBottom: '1px solid lightgrey',
          }}
        >
          <Typography variant="h6" component="div">
            Guida per la tabella Stakeholders
          </Typography>
          <IconButton onClick={toggleDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          {accordionSections.map((section) => (
            (!section.adminOnly || isCurrentUserAdmin) && (
              <Accordion
                key={section.id}
                expanded={expandedAccordion === section.id}
                onChange={handleAccordionChange(section.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${section.id}-content`}
                  id={`${section.id}-header`}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{section.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {section.content}
                </AccordionDetails>
              </Accordion>
            )
          ))}
        </Box>
      </Drawer>
    </>
  );
};

export default HelpGuideDrawerStakeholders;